import React, { useMemo, useState } from "react";
import { View, StyleSheet } from "react-native";
import NtText from "../../../components/NtText";
import NtButton from "../../../components/NtButton";
import NtTextInput from "../../../components/NtTextInput";
import NtTouchableEffect from "../../../components/NtTouchableEffect";
import NtMaterialIcon from "../../../components/NtMaterialIcon";
import NtOnboardingEditorialSection from "../../../onboarding/components/NtOnboardingEditorialSection";
import NtOnboardingCountryPicker from "../../../onboarding/components/NtOnboardingCountryPicker";
import NtModalInputTitle from "../../../components/NtModalInputTitle";
import { useTheme } from "../../../context/ThemeContext";

// Placeholder data until the backend number-pool API is ready. Keeps the UI
// realistic and lets the user feel the interaction. Replace `MOCK_NUMBERS`
// with a fetcher call once the endpoint exists.
const MOCK_NUMBERS = [
  { number: "+1 (305) 555-0184", location: "Miami, FL",       caps: { voice: true,  sms: true,  mms: false }, price: "$1.15 / mo" },
  { number: "+1 (305) 555-0192", location: "Miami, FL",       caps: { voice: true,  sms: true,  mms: true  }, price: "$1.15 / mo" },
  { number: "+1 (305) 555-0207", location: "Miami, FL",       caps: { voice: true,  sms: false, mms: false }, price: "$1.15 / mo" },
  { number: "+1 (305) 555-0218", location: "Miami, FL",       caps: { voice: true,  sms: true,  mms: false }, price: "$1.15 / mo" },
  { number: "+1 (305) 555-0243", location: "Hialeah, FL",     caps: { voice: true,  sms: true,  mms: false }, price: "$1.15 / mo" },
  { number: "+1 (305) 555-0276", location: "Coral Gables, FL",caps: { voice: true,  sms: true,  mms: true  }, price: "$1.15 / mo" },
  { number: "+1 (305) 555-0299", location: "Miami Beach, FL", caps: { voice: true,  sms: true,  mms: false }, price: "$1.15 / mo" },
  { number: "+1 (305) 555-0318", location: "Doral, FL",       caps: { voice: true,  sms: true,  mms: true  }, price: "$1.15 / mo" },
];

function CapChip({ label, on }) {
  const { theme } = useTheme();
  const styles = useMemo(() => makeCapStyles(theme), [theme]);
  return (
    <View style={[styles.chip, on && styles.chipOn]}>
      <NtText style={[styles.text, on && styles.textOn]}>{label}</NtText>
    </View>
  );
}

function NtSetupPhoneNumberStep({ search, selectedNumber, onChangeSearch, onSelect }) {
  const { theme } = useTheme();
  const styles = useMemo(() => makeStyles(theme), [theme]);

  const updateSearch = (patch) =>
    onChangeSearch && onChangeSearch({ ...search, ...patch });

  return (
    <View>
      {selectedNumber ? (
        <View style={styles.selectedBanner}>
          <View style={styles.selectedIc}>
            <NtMaterialIcon name="check" size={18} color="#fff" />
          </View>
          <View>
            <NtText style={styles.selectedLabel}>Selected</NtText>
            <NtText style={styles.selectedNum}>{selectedNumber}</NtText>
          </View>
          <NtTouchableEffect
            style={styles.changeBtn}
            onPress={() => onSelect && onSelect(null)}
          >
            <NtText style={styles.changeText}>Change</NtText>
          </NtTouchableEffect>
        </View>
      ) : null}

      <NtOnboardingEditorialSection
        first
        label="Search numbers"
        caption="Filter by country, area code and number type."
      >
        <View style={styles.row}>
          <View style={styles.cell}>
            <NtOnboardingCountryPicker
              title="Country *"
              value={search?.country || "United States"}
              onChange={(country) => updateSearch({ country })}
              placeholder="Select a country"
            />
          </View>
          <View style={styles.cell}>
            <NtModalInputTitle style={styles.fieldTitle}>
              Area code or pattern
            </NtModalInputTitle>
            <NtTextInput
              icon="magnify"
              placeholder="305, MAIN, etc."
              value={search?.pattern || ""}
              onChangeText={(pattern) => updateSearch({ pattern })}
            />
          </View>
        </View>

        <View style={styles.row}>
          <View style={styles.cell}>
            <NtModalInputTitle style={styles.fieldTitle}>Number type</NtModalInputTitle>
            <View style={styles.toggle}>
              <NtTouchableEffect
                style={[
                  styles.toggleBtn,
                  (search?.type || "local") === "local" && styles.toggleBtnActive,
                ]}
                onPress={() => updateSearch({ type: "local" })}
              >
                <NtText
                  style={[
                    styles.toggleText,
                    (search?.type || "local") === "local" && styles.toggleTextActive,
                  ]}
                >
                  Local
                </NtText>
              </NtTouchableEffect>
              <NtTouchableEffect
                style={[
                  styles.toggleBtn,
                  search?.type === "tollfree" && styles.toggleBtnActive,
                ]}
                onPress={() => updateSearch({ type: "tollfree" })}
              >
                <NtText
                  style={[
                    styles.toggleText,
                    search?.type === "tollfree" && styles.toggleTextActive,
                  ]}
                >
                  Toll-free
                </NtText>
              </NtTouchableEffect>
            </View>
          </View>
          <View style={[styles.cell, styles.searchBtnCell]}>
            <NtButton text="Search" icon="magnify" onPress={() => {}} />
          </View>
        </View>
      </NtOnboardingEditorialSection>

      <NtOnboardingEditorialSection
        label="Available numbers"
        caption="Click a row to select. Capabilities show what each number supports."
      >
        <View style={styles.resultsHead}>
          <NtText style={styles.resultsCount}>
            <NtText style={styles.resultsAccent}>{MOCK_NUMBERS.length}</NtText>
            {" "}available in{" "}
            <NtText style={styles.resultsStrong}>Miami, FL</NtText>
          </NtText>
        </View>

        <View style={styles.list}>
          {MOCK_NUMBERS.map((item, index) => {
            const isSelected = selectedNumber === item.number;
            const isLast = index === MOCK_NUMBERS.length - 1;
            return (
              <NtTouchableEffect
                key={item.number}
                style={[
                  styles.numberRow,
                  isLast && styles.numberRowLast,
                  isSelected && styles.numberRowSelected,
                ]}
                onPress={() => onSelect && onSelect(item.number)}
              >
                <View
                  style={[
                    styles.pick,
                    isSelected && styles.pickSelected,
                  ]}
                >
                  {isSelected ? (
                    <NtMaterialIcon name="check" size={12} color="#fff" />
                  ) : null}
                </View>
                <NtText
                  style={[
                    styles.numberText,
                    isSelected && styles.numberTextSelected,
                  ]}
                >
                  {item.number}
                </NtText>
                <NtText style={styles.loc}>{item.location}</NtText>
                <View style={styles.caps}>
                  <CapChip label="Voice" on={item.caps.voice} />
                  <CapChip label="SMS" on={item.caps.sms} />
                  <CapChip label="MMS" on={item.caps.mms} />
                </View>
                <NtText style={styles.price}>{item.price}</NtText>
              </NtTouchableEffect>
            );
          })}
        </View>
      </NtOnboardingEditorialSection>
    </View>
  );
}

const makeStyles = (t) =>
  StyleSheet.create({
    row: { flexDirection: "row", gap: 14, marginBottom: 4 },
    cell: { flex: 1 },
    searchBtnCell: { alignSelf: "flex-end" },

    fieldTitle: { marginLeft: 4, marginBottom: 4 },

    toggle: {
      flexDirection: "row",
      backgroundColor: t.surface2,
      borderWidth: 1,
      borderColor: t.borderSoft,
      borderRadius: t.radius,
      height: 38,
      alignSelf: "flex-start",
      overflow: "hidden",
    },
    toggleBtn: {
      paddingHorizontal: 18,
      justifyContent: "center",
    },
    toggleBtnActive: { backgroundColor: t.brand },
    toggleText: { fontSize: 12, fontWeight: "600", color: t.textDim },
    toggleTextActive: { color: "#ffffff" },

    selectedBanner: {
      flexDirection: "row",
      alignItems: "center",
      gap: 14,
      backgroundColor: t.pillBg,
      borderWidth: 1,
      borderColor: t.pillBorder,
      borderRadius: t.radiusCard,
      paddingVertical: 14,
      paddingHorizontal: 18,
      marginBottom: 18,
      maxWidth: 920,
    },
    selectedIc: {
      width: 32,
      height: 32,
      borderRadius: 16,
      backgroundColor: t.brand2,
      alignItems: "center",
      justifyContent: "center",
    },
    selectedLabel: {
      fontSize: 11,
      textTransform: "uppercase",
      letterSpacing: 0.6,
      fontWeight: "700",
      color: t.brand2,
    },
    selectedNum: {
      fontFamily: "Menlo",
      fontSize: 16,
      fontWeight: "700",
      color: t.text,
      letterSpacing: 0.5,
      marginTop: 2,
    },
    changeBtn: { marginLeft: "auto", padding: 4 },
    changeText: { fontSize: 12, color: t.brand2, fontWeight: "600" },

    resultsHead: { paddingBottom: 10 },
    resultsCount: { fontSize: 12, color: t.textDim, fontWeight: "500" },
    resultsAccent: { color: t.brand2, fontWeight: "700" },
    resultsStrong: { color: t.text, fontWeight: "700" },

    list: {
      backgroundColor: t.surface2,
      borderWidth: 1,
      borderColor: t.border,
      borderRadius: t.radiusCard,
      overflow: "hidden",
    },
    numberRow: {
      flexDirection: "row",
      alignItems: "center",
      gap: 14,
      paddingVertical: 12,
      paddingHorizontal: 16,
      borderBottomWidth: 1,
      borderBottomColor: t.borderSoft,
    },
    numberRowLast: { borderBottomWidth: 0 },
    numberRowSelected: { backgroundColor: t.pillBg },
    pick: {
      width: 18,
      height: 18,
      borderRadius: 9,
      borderWidth: 1.5,
      borderColor: t.border,
      backgroundColor: t.surface,
      alignItems: "center",
      justifyContent: "center",
    },
    pickSelected: {
      backgroundColor: t.brand2,
      borderColor: t.brand2,
    },
    numberText: {
      fontFamily: "Menlo",
      fontSize: 14,
      fontWeight: "700",
      color: t.text,
      flex: 1,
      letterSpacing: 0.5,
    },
    numberTextSelected: { color: t.brand2 },
    loc: { fontSize: 12, color: t.textDim, minWidth: 130 },
    caps: { flexDirection: "row", gap: 4, marginRight: 16 },
    price: {
      fontSize: 12,
      color: t.textDim,
      minWidth: 70,
      textAlign: "right",
    },
  });

const makeCapStyles = (t) =>
  StyleSheet.create({
    chip: {
      paddingVertical: 2,
      paddingHorizontal: 7,
      borderRadius: 999,
      backgroundColor: t.surface3,
      borderWidth: 1,
      borderColor: t.border,
    },
    chipOn: {
      backgroundColor: "rgba(76,183,130,0.12)",
      borderColor: "rgba(76,183,130,0.3)",
    },
    text: {
      fontSize: 10,
      fontWeight: "700",
      color: t.textMute,
      letterSpacing: 0.4,
      textTransform: "uppercase",
    },
    textOn: { color: t.green },
  });

export default NtSetupPhoneNumberStep;
